import { useState, useEffect } from 'react';
import { Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Organizations = () => {
  const [committee, setCommittee] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/committee`)
      .then((res) => res.json())
      .then((data) => {
        setCommittee(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/6j83625y_DSC_5878-2048x1254.jpg"
            alt="Paws in the Park Sub Committee 2025 at Moorings Park"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-cyan-50/90 to-white"></div>
        </div>

        <div className="relative container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto text-center">
            <Badge className="mb-4 bg-cyan-100 text-cyan-800 hover:bg-cyan-200 border-cyan-200">
              Event Committee
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight">
              Paws in the Park TWRA Subcommittee 2026
            </h1>
          </div>
        </div>
      </section>

      {/* Committee Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                Event Committee
              </h2>
              <p className="text-lg text-slate-600">
                Our passionate volunteers working tirelessly to create a memorable experience
              </p>
            </div>

            {loading ? (
              <p className="text-center text-slate-500">Loading committee...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {committee.map((member) => (
                  <Card
                    key={member.id || member.name}
                    className="border-slate-200 hover:border-cyan-300 hover:shadow-lg transition-all duration-300"
                    data-testid={`committee-card-${member.name}`}
                  >
                    <CardHeader>
                      <div className="flex items-start space-x-4">
                        <div className="bg-gradient-to-br from-cyan-100 to-teal-100 w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xl font-bold text-cyan-700">
                            {member.name.split(' ').map((n) => n[0]).join('')}
                          </span>
                        </div>
                        <div className="flex-1">
                          <CardTitle className="text-xl">{member.name}</CardTitle>
                          {member.role && (
                            <Badge variant="outline" className="mt-1 bg-cyan-50 text-cyan-700 border-cyan-200">
                              {member.role}
                            </Badge>
                          )}
                          {member.bio && (
                            <p className="text-sm text-slate-600 mt-3 leading-relaxed">{member.bio}</p>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            )}

            {/* Committee in action photo */}
            <div className="mt-12 rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-white">
              <img
                src="https://customer-assets.emergentagent.com/job_comp-central-1/artifacts/pd8i8286_4737F74C-442E-4DEC-A103-75A346ACAF41_1_105_c.jpeg"
                alt="Our committee at work — Paws in the Park volunteers running the day"
                className="w-full h-auto max-h-[600px] object-contain"
              />
              <div className="p-4 bg-white">
                <p className="text-center text-sm text-slate-600 italic">
                  Our committee volunteers in action on event day.
                </p>
              </div>
            </div>

            <Card className="mt-8 bg-gradient-to-br from-cyan-50 to-white border-cyan-200">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="h-6 w-6 text-cyan-700 mr-2" />
                  Hosted By
                </CardTitle>
              </CardHeader>
              <CardContent>
                <a
                  href="https://twra.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-cyan-700 hover:text-cyan-800 hover:underline mb-2 inline-block"
                  data-testid="twra-website-link"
                >
                  Twin Waters Residents Association (TWRA) ↗
                </a>
                <p className="text-slate-600 mt-2">
                  A community-driven organization dedicated to bringing residents together
                  and supporting local initiatives that make Twin Waters a better place to live.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Organizations;
